<template>
  <div class="kpi-section">
    <div class="kpi-head">
      <div>
        <div class="kpi-crumb">Indicadores</div>
        <h2 class="kpi-title">Tiempos del trámite</h2>
        <p class="kpi-subtitle">
          Días calendario desde que se recibe el documento hasta que el trámite
          se entrega al cliente final, desglosado por tramo del ciclo.
        </p>
      </div>
    </div>

    <!-- Filtros de la sección: rango de cierre del trámite y meta de días. -->
    <v-layout row wrap class="kpi-filters">
      <v-flex md3 sm6 xs12>
        <v-menu
          :close-on-content-click="false"
          v-model="startMenu"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="startDate"
            label="Desde"
            prepend-icon="event"
            readonly
          ></v-text-field>
          <v-date-picker
            v-model="startDate"
            locale="es-es"
            color="#254f87"
            no-title
            @input="startMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-menu
          :close-on-content-click="false"
          v-model="endMenu"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="endDate"
            label="Hasta"
            prepend-icon="event"
            readonly
          ></v-text-field>
          <v-date-picker
            v-model="endDate"
            locale="es-es"
            color="#254f87"
            no-title
            @input="endMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-select
          :items="metaOptions"
          v-model="metaDias"
          label="Meta del ciclo (días)"
          prepend-icon="flag"
        ></v-select>
      </v-flex>

      <v-flex md3 sm6 xs12 class="kpi-actions">
        <v-btn class="kpi-btn" depressed @click="load">
          <v-icon left small>refresh</v-icon>Actualizar
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- El tramo de entrega sólo existe desde que se empezó a registrar la
         fecha de entrega; mientras tanto el KPI principal es el ciclo parcial. -->
    <v-alert
      v-if="meta && meta.usarCicloParcial"
      :value="true"
      color="#E6C57D"
      icon="info"
      class="kpi-alert"
    >
      Aún no hay trámites con fecha de entrega registrada, por lo que el ciclo
      se mide hasta la salida de Farmacia y Drogas. El tramo
      <strong>Salida → Entrega</strong> se completará automáticamente conforme
      se marquen las facturas como entregadas.
    </v-alert>

    <!-- Tarjetas de resumen. El ciclo es el número principal; los tramos
         explican de dónde salen esos días. -->
    <v-layout row wrap v-if="resumen">
      <v-flex lg4 sm6 xs12>
        <div class="kpi-tile kpi-tile--hero">
          <span class="kpi-tile__label">{{ cicloLabel }}</span>
          <span class="kpi-tile__value">
            {{ format(ciclo.avg) }}<small>días</small>
          </span>
          <span class="kpi-tile__foot">
            {{ ciclo.n }} trámites · máx {{ format(ciclo.max) }} días
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm6 xs12>
        <div class="kpi-tile">
          <span class="kpi-tile__label">Dentro de meta</span>
          <span class="kpi-tile__value">
            {{ ciclo.pctDentroMeta === null ? '—' : ciclo.pctDentroMeta
            }}<small>%</small>
          </span>
          <span class="kpi-tile__foot">
            {{ ciclo.dentroMeta }} de {{ ciclo.n }} en ≤ {{ metaDias }} días
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm4 xs12>
        <div class="kpi-tile">
          <span class="kpi-swatch" :style="{ background: COLORS[0] }"></span>
          <span class="kpi-tile__label">Recepción → Trámite</span>
          <span class="kpi-tile__value">
            {{ format(resumen.recepcionATramite.avg) }}<small>días</small>
          </span>
          <span class="kpi-tile__foot">
            máx {{ format(resumen.recepcionATramite.max) }} días
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm4 xs12>
        <div class="kpi-tile">
          <span class="kpi-swatch" :style="{ background: COLORS[1] }"></span>
          <span class="kpi-tile__label">En Farmacia y Drogas</span>
          <span class="kpi-tile__value">
            {{ format(resumen.enFd.avg) }}<small>días</small>
          </span>
          <span class="kpi-tile__foot">
            máx {{ format(resumen.enFd.max) }} días
          </span>
        </div>
      </v-flex>

      <v-flex lg2 sm4 xs12>
        <div class="kpi-tile">
          <span class="kpi-swatch" :style="{ background: COLORS[2] }"></span>
          <span class="kpi-tile__label">Salida → Entrega</span>
          <span class="kpi-tile__value">
            {{ format(resumen.salidaAEntrega.avg) }}<small>días</small>
          </span>
          <span class="kpi-tile__foot">
            {{ resumen.salidaAEntrega.n }} entregas registradas
          </span>
        </div>
      </v-flex>

      <v-flex lg12 sm12 xs12 v-if="resumen.abiertos.n">
        <div class="kpi-tile kpi-tile--wide">
          <span class="kpi-tile__label">Trámites aún abiertos</span>
          <span class="kpi-tile__value">
            {{ resumen.abiertos.n }}<small>en curso</small>
          </span>
          <span class="kpi-tile__foot">
            Antigüedad promedio {{ format(resumen.abiertos.avgEdad) }} días ·
            <strong>{{ resumen.abiertos.fueraMeta }}</strong> ya superaron la
            meta de {{ metaDias }} días · el más antiguo lleva
            {{ format(resumen.abiertos.maxEdad) }} días
          </span>
        </div>
      </v-flex>
    </v-layout>

    <!-- Tendencia: barras apiladas, un segmento por tramo del ciclo. La rampa
         de azules va de claro a oscuro siguiendo el orden de las etapas. -->
    <v-layout row wrap>
      <v-flex lg12 sm12 xs12 v-if="chartData">
        <v-widget
          content-bg="white"
          title="Días promedio por tramo del ciclo (por mes de cierre)"
        >
          <div slot="widget-content">
            <e-chart
              :path-option="chartOptions"
              width="90%"
              height="360px"
            ></e-chart>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>

    <!-- Motivos de pendiente: cuánto tiempo detuvieron el trámite. -->
    <v-layout row wrap v-if="motivos && motivos.length">
      <v-flex lg6 sm12 xs12>
        <v-widget content-bg="white" title="Motivos que detuvieron el trámite">
          <div slot="widget-content">
            <table class="kpi-table">
              <thead>
                <tr>
                  <th>Motivo</th>
                  <th class="num">Veces</th>
                  <th class="num">Abiertos</th>
                  <th class="num">Días prom.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in motivos" :key="m.motivo">
                  <td>{{ m.motivo }}</td>
                  <td class="num">{{ m.n }}</td>
                  <td class="num">
                    <span v-if="m.abiertos" class="kpi-pill">{{
                      m.abiertos
                    }}</span>
                    <span v-else>—</span>
                  </td>
                  <td class="num">{{ format(m.avgDiasDetenido) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-widget>
      </v-flex>

      <v-flex lg6 sm12 xs12>
        <v-widget content-bg="white" title="Trámites más lentos del periodo">
          <div slot="widget-content">
            <table class="kpi-table">
              <thead>
                <tr>
                  <th># Factura</th>
                  <th>Laboratorio</th>
                  <th class="num">Días</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in lentos" :key="t.id">
                  <td>{{ t.invoiceNumber }}</td>
                  <td class="kpi-muted">{{ t.laboratorio || '—' }}</td>
                  <td class="num">
                    <span
                      :class="[
                        'kpi-pill',
                        t.diasCiclo > metaDias ? 'kpi-pill--warn' : '',
                      ]"
                    >
                      {{ t.diasCiclo }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!lentos.length">
                  <td colspan="3" class="kpi-muted">
                    Sin datos en el periodo.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import EChart from '@/components/chart/echart'
import VWidget from '@/components/VWidget'

// Rampa ordinal en el azul de marca: de claro a oscuro siguiendo el orden de
// las etapas del ciclo. Validada para contraste y separación de pasos.
const COLORS = ['#8FB6E0', '#4A80BA', '#254F87']
const SERIES = [
  'Recepción → Trámite',
  'En Farmacia y Drogas',
  'Salida → Entrega',
]
const SURFACE = '#ffffff'

export default {
  components: {
    VWidget,
    EChart,
  },

  props: {
    // Filtro de laboratorio que ya aplica el Dashboard, para que la sección
    // respete la misma selección.
    labId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      COLORS,
      startMenu: false,
      endMenu: false,
      startDate: moment()
        .subtract(11, 'months')
        .startOf('month')
        .format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      metaDias: 7,
      metaOptions: [3, 5, 7, 10, 15, 30],
      meta: null,
      resumen: null,
      tendencia: [],
      motivos: [],
      lentos: [],
      chartData: null,
    }
  },

  computed: {
    // Mientras no haya entregas registradas el ciclo se mide hasta la salida
    // de F&D, y la tarjeta principal lo dice explícitamente.
    usarParcial() {
      return !!(this.meta && this.meta.usarCicloParcial)
    },
    ciclo() {
      if (!this.resumen) return {}
      return this.usarParcial
        ? this.resumen.cicloParcial
        : this.resumen.cicloTotal
    },
    cicloLabel() {
      return this.usarParcial
        ? 'Ciclo recepción → salida de F&D'
        : 'Ciclo completo recepción → entrega'
    },
    // Sólo se grafican los tramos que tienen dato, para no dibujar una serie
    // vacía cuando aún no se registra la entrega.
    activeSeries() {
      return this.usarParcial ? SERIES.slice(0, 2) : SERIES
    },
    chartOptions() {
      const last = this.activeSeries.length - 1
      const options = [
        ['dataset.source', this.chartData],
        ['color', COLORS.slice(0, this.activeSeries.length)],
        ['legend.show', true],
        ['legend.bottom', 0],
        ['legend.icon', 'roundRect'],
        ['grid.left', 48],
        ['grid.right', 24],
        ['grid.top', 24],
        ['grid.bottom', 56],
        ['tooltip.trigger', 'axis'],
        ['tooltip.axisPointer.type', 'shadow'],
        ['xAxis.type', 'category'],
        ['xAxis.axisLabel.show', true],
        ['xAxis.axisTick.show', false],
        ['xAxis.axisLine.lineStyle.color', '#e2e8f0'],
        ['yAxis.type', 'value'],
        ['yAxis.name', 'días'],
        ['yAxis.nameTextStyle.color', '#64748b'],
        ['yAxis.axisLabel.show', true],
        ['yAxis.axisLine.show', false],
        ['yAxis.axisTick.show', false],
        ['yAxis.splitLine.lineStyle.color', '#eef2f7'],
      ]

      this.activeSeries.forEach((name, index) => {
        options.push([`series[${index}].type`, 'bar'])
        options.push([`series[${index}].stack`, 'dias'])
        options.push([`series[${index}].barMaxWidth`, 28])
        // Un hilo de superficie entre segmentos: separa la pila sin cambiar
        // el color de los tramos.
        options.push([`series[${index}].itemStyle.barBorderColor`, SURFACE])
        options.push([`series[${index}].itemStyle.barBorderWidth`, 2])
      })
      // Extremo redondeado sólo en el tope de la pila.
      options.push([`series[${last}].itemStyle.barBorderRadius`, [4, 4, 0, 0]])
      // Línea de meta, etiquetada: la única referencia numérica en el lienzo.
      options.push([
        `series[${last}].markLine`,
        {
          silent: true,
          symbol: 'none',
          lineStyle: { color: '#bd614d', type: 'dashed', width: 2 },
          label: {
            formatter: `Meta ${this.metaDias}d`,
            position: 'insideEndTop',
            color: '#bd614d',
          },
          data: [{ yAxis: this.metaDias }],
        },
      ])

      return options
    },
  },

  watch: {
    labId() {
      this.load()
    },
  },

  created() {
    this.load()
  },

  methods: {
    format(value) {
      return value === null || value === undefined ? '—' : value
    },

    params() {
      const params = {
        startDate: this.startDate,
        endDate: this.endDate,
        metaDias: this.metaDias,
      }
      if (this.labId) {
        params.labId = this.labId
      }
      return params
    },

    load() {
      // e-chart lee sus opciones una sola vez al montar: hay que desmontarlo
      // (null) para que el v-if lo vuelva a crear con los datos nuevos.
      this.chartData = null

      this.$store
        .dispatch('dashboard/getKpiTramite', this.params())
        .then(response => {
          const data = response && response.data
          if (!data) return
          this.meta = data.meta
          this.resumen = data.resumen
          this.tendencia = data.tendencia || []
          this.motivos = data.motivos || []

          const usarParcial = data.meta.usarCicloParcial
          this.chartData = this.tendencia.map(row => {
            const point = {
              mes: row.label,
              [SERIES[0]]: row.recepcionATramite || 0,
              [SERIES[1]]: row.enFd || 0,
            }
            if (!usarParcial) {
              point[SERIES[2]] = row.salidaAEntrega || 0
            }
            return point
          })
        })

      this.$store
        .dispatch('dashboard/getKpiTramitesLentos', {
          ...this.params(),
          limit: 8,
        })
        .then(response => {
          this.lentos = (response && response.data) || []
        })
    },
  },
}
</script>

<style scoped>
.kpi-section {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}
.kpi-crumb {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}
.kpi-title {
  font-size: 22px;
  font-weight: 600;
  color: #0f172a;
  margin: 2px 0 4px;
}
.kpi-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  max-width: 640px;
}
.kpi-filters {
  margin-top: 8px;
}
.kpi-actions {
  display: flex;
  align-items: center;
}
.kpi-btn {
  background: #254f87 !important;
  color: #fff !important;
  margin: 0;
}
.kpi-alert {
  color: #0f172a;
  font-size: 13px;
  margin: 8px 0 16px;
}

.kpi-tile {
  position: relative;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 16px 18px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.kpi-tile--hero {
  background: #254f87;
  border-color: #254f87;
}
.kpi-tile--hero .kpi-tile__label,
.kpi-tile--hero .kpi-tile__foot {
  color: rgba(255, 255, 255, 0.72);
}
.kpi-tile--hero .kpi-tile__value {
  color: #fff;
}
.kpi-tile__label {
  font-size: 12px;
  color: #64748b;
  line-height: 1.3;
}
.kpi-tile__value {
  font-size: 30px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.15;
  margin-top: 4px;
}
.kpi-tile--hero .kpi-tile__value {
  font-size: 40px;
}
.kpi-tile__value small {
  font-size: 13px;
  font-weight: 400;
  margin-left: 5px;
  opacity: 0.72;
}
.kpi-tile__foot {
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
}
.kpi-swatch {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.kpi-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.kpi-table th {
  text-align: left;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
}
.kpi-table td {
  padding: 9px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
}
.kpi-table .num {
  text-align: right;
}
.kpi-muted {
  color: #64748b;
}
.kpi-pill {
  display: inline-block;
  min-width: 30px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef2f7;
  color: #254f87;
  font-weight: 600;
}
.kpi-pill--warn {
  background: #f7e7e3;
  color: #bd614d;
}
</style>
